import { z } from 'zod';
import { MyAvailability, PersonAvailability } from './notification-prefs.js';
/**
 * People's status (docs/designs/mobile/CHAT_CHANGES.md, "People row and status"). Workspace-scoped,
 * expires on its own, shown on the person's circle, in DM headers, the sidebar and next to mentions.
 */
export const STATUS_PRESETS = ['meeting', 'focus', 'lunch', 'holiday', 'done', 'dnd', 'commuting', 'sick', 'celebrating'];
export const StatusPreset = z.enum(STATUS_PRESETS);
/** Labels in the Set status sheet; also the sentence form "Muhammad is in a meeting until 3:00". */
export const statusPresets = {
    meeting: { label: 'In a meeting', phrase: 'is in a meeting', emoji: '📅' },
    focus: { label: 'Focus time', phrase: 'is focusing', emoji: '🎧' },
    lunch: { label: 'Lunch break', phrase: 'is on a lunch break', emoji: '☕' },
    holiday: { label: 'On holiday', phrase: 'is on holiday', emoji: '🌴' },
    done: { label: 'Done for today', phrase: 'is done for today', emoji: '🌙' },
    dnd: { label: 'Do not disturb', phrase: 'does not want to be disturbed', emoji: '⛔' },
    commuting: { label: 'Commuting', phrase: 'is commuting', emoji: '🚆' },
    sick: { label: 'Off sick', phrase: 'is off sick', emoji: '🤒' },
    celebrating: { label: 'Celebrating', phrase: 'is celebrating', emoji: '🎉' },
};
/** A single emoji: a few code points (skin tones, joiners), no letters or digits. */
export const StatusEmoji = z
    .string()
    .min(1)
    .max(16)
    .refine((v) => /\p{Extended_Pictographic}/u.test(v) && !/[\p{L}\p{N}\s<>]/u.test(v), 'Pick one emoji');
export const UserStatus = z.object({
    userId: z.uuid(),
    preset: StatusPreset.nullable(),
    message: z.string(),
    /** A custom emoji ("add something else"); shown instead of the preset's. Optional for older servers. */
    emoji: z.string().nullable().optional(),
    /** When the status clears itself; null means it stays until cleared. */
    until: z.string().nullable(),
    pauseNotifications: z.boolean(),
    updatedAt: z.string(),
});
export const SetStatusInput = z
    .object({
    preset: StatusPreset.nullable().optional(),
    message: z.string().trim().max(100),
    /** One emoji of the person's own choosing. */
    emoji: StatusEmoji.nullable().optional(),
    /** Computed by the client from "Clear after" (30 min, 1 hour, Today in their time zone, Custom). */
    until: z.iso.datetime({ offset: true }).nullable(),
    pauseNotifications: z.boolean().optional(),
})
    .refine((v) => !!v.preset || v.message.length > 0 || !!v.emoji, { message: 'Pick a status or write a message', path: ['message'] });
export const PeopleStatuses = z.object({
    /** Current statuses of the people the caller can see. Expired statuses are left out. */
    items: z.array(UserStatus),
    /** People the caller asked to hear about when they're free. */
    watching: z.array(z.uuid()),
    /** Busy, away and paused people the caller can see (docs/adr/0048). Optional for older servers. */
    availability: z.array(PersonAvailability).optional(),
    /** The caller's own presence choice and pause. */
    mine: MyAvailability.optional(),
    /** People the caller can see who are in a catch-up right now ("In a catch-up"; never which channel). */
    inCatchUp: z.array(z.uuid()).optional(),
});
export const StatusWatchInput = z.object({ watching: z.boolean() });
/** "Muhammad is in a meeting until 3:00": the sentence shown next to a mention or in a DM header. */
export function statusSentence(name, s, formatTime) {
    const first = name.split(' ')[0] ?? name;
    const what = s.preset ? `${first} ${statusPresets[s.preset].phrase}` : `${first}: ${s.message || 'busy'}`;
    return s.until ? `${what} until ${formatTime(s.until)}` : what;
}
/**
 * Emoji offered for a status of your own ("add something else" in the Set status sheet), with names to
 * search by. Shared by the web and phone pickers; any single emoji typed in is accepted too.
 */
export const STATUS_EMOJI = [
    { emoji: '💬', name: 'speech chat talking' },
    { emoji: '📅', name: 'calendar meeting' },
    { emoji: '🎧', name: 'headphones focus music' },
    { emoji: '💻', name: 'laptop working computer' },
    { emoji: '🏠', name: 'home house remote' },
    { emoji: '🏢', name: 'office building' },
    { emoji: '✈️', name: 'plane travel flight' },
    { emoji: '🚗', name: 'car driving' },
    { emoji: '🚆', name: 'train commuting' },
    { emoji: '🚲', name: 'bike cycling' },
    { emoji: '☕', name: 'coffee break' },
    { emoji: '🍽️', name: 'lunch dinner food' },
    { emoji: '🍕', name: 'pizza food' },
    { emoji: '🌴', name: 'palm holiday vacation' },
    { emoji: '🏖️', name: 'beach holiday' },
    { emoji: '🤒', name: 'sick ill' },
    { emoji: '🤧', name: 'cold sneeze sick' },
    { emoji: '🩺', name: 'doctor appointment' },
    { emoji: '🦷', name: 'dentist tooth' },
    { emoji: '👶', name: 'baby parental leave' },
    { emoji: '🎓', name: 'study course learning' },
    { emoji: '📚', name: 'books reading' },
    { emoji: '🧠', name: 'brain thinking deep work' },
    { emoji: '✍️', name: 'writing' },
    { emoji: '🎨', name: 'design art' },
    { emoji: '🐛', name: 'bug fixing' },
    { emoji: '🚀', name: 'rocket launch release' },
    { emoji: '🔥', name: 'fire urgent' },
    { emoji: '⏳', name: 'hourglass waiting' },
    { emoji: '🎉', name: 'party celebrate' },
    { emoji: '🎂', name: 'birthday cake' },
    { emoji: '🏃', name: 'running exercise' },
    { emoji: '🧘', name: 'yoga wellbeing' },
    { emoji: '🏋️', name: 'gym workout' },
    { emoji: '⚽', name: 'football sport' },
    { emoji: '🌙', name: 'moon night done' },
    { emoji: '😴', name: 'sleeping tired' },
    { emoji: '🤝', name: 'handshake client' },
    { emoji: '📞', name: 'phone call' },
    { emoji: '🎤', name: 'microphone presenting talk' },
    { emoji: '🎥', name: 'video recording' },
    { emoji: '📦', name: 'package moving' },
    { emoji: '🔧', name: 'wrench fixing maintenance' },
    { emoji: '🙏', name: 'thanks please' },
    { emoji: '👀', name: 'eyes looking reviewing' },
    { emoji: '✅', name: 'done check' },
    { emoji: '⛔', name: 'stop do not disturb' },
    { emoji: '❤️', name: 'heart love' },
];
/** Emoji whose names contain every word typed, in list order. */
export function searchStatusEmoji(query) {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    return words.length ? STATUS_EMOJI.filter((e) => words.every((w) => e.name.includes(w))) : [...STATUS_EMOJI];
}
//# sourceMappingURL=status.js.map